import Component from '@glimmer/component'
import type Store from '@ember-data/store'
import { service } from '@ember/service'
import { task, timeout } from 'ember-concurrency'
import { waitFor } from '@ember/test-waiters'
import type BookLibraryShare from 'teacher-ui-client/models/book-library/share'
import { ShareStatus } from 'teacher-ui-client/models/book-library/share'
import perform from 'ember-concurrency/helpers/perform'
import { doAction } from 'teacher-ui-client/modifiers/do-action'
import { t } from 'ember-intl'

export interface Args {
  collectionId: string
  hidePendingShareCount?: boolean
}

interface Signature {
  Element: HTMLDListElement
  Args: Args
}

export interface ShareCounts {
  activeShares: number
  pendingShares: number
}

export class BookLibraryCollectionShareCounts extends Component<Signature> {
  @service store!: Store

  get query() {
    return {
      filter: {
        status: [ShareStatus.Pending, ShareStatus.Active].join(','),
        collection_id: this.args.collectionId,
      },
    }
  }

  get shareCounts() {
    return this.queryShareCounts.last?.value
  }

  queryShareCounts = task(
    { restartable: true },
    waitFor(async (query: typeof this.query) => {
      await timeout(100)
      return (await this.store.query<BookLibraryShare>('book-library/share', query)).reduce(
        (previousValue: ShareCounts, currentValue: BookLibraryShare) => {
          switch (currentValue.status) {
            case ShareStatus.Pending:
              return {
                ...previousValue,
                pendingShares: previousValue.pendingShares + 1,
              }
            case ShareStatus.Active:
              return {
                ...previousValue,
                activeShares: previousValue.activeShares + 1,
              }
            default:
              return previousValue
          }
        },
        { activeShares: 0, pendingShares: 0 } as const,
      )
    }),
  )

  get showPendingShareCount() {
    return this.args.hidePendingShareCount !== true
  }

  <template>
    <dl
      class="inline-grid grid-cols-[max-content_max-content] gap-x-1 text-right font-semibold"
      ...attributes
      {{doAction (perform this.queryShareCounts this.query)}}
    >
      <dt class="text-dusty-black-250">{{t "bookLibrary.components.collectionShareCounts.active"}}</dt>
      <dd class="min-w-4 text-black" data-test-active-shares>
        {{#if this.shareCounts}}
          {{this.shareCounts.activeShares}}
        {{else}}
          -
        {{/if}}
      </dd>
      {{#if this.showPendingShareCount}}
        <dt class="text-dusty-black-250">{{t "bookLibrary.components.collectionShareCounts.pending"}}</dt>
        <dd class="min-w-4 text-black" data-test-pending-shares>
          {{#if this.shareCounts}}
            {{this.shareCounts.pendingShares}}
          {{else}}
            -
          {{/if}}
        </dd>
      {{/if}}
    </dl>
  </template>
}

export default BookLibraryCollectionShareCounts
